import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, FilePlus2, RefreshCw, Stamp } from 'lucide-react';
import { EMPTY_STATES, ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, api, apiData } from '@/lib/api';
import { formatFullDate } from '@/lib/format';
import { useCanWrite, useRole } from '@/store/auth';
import type { ChildSummary, LifeRecord } from '@/types';

const STATUS_LABEL: Record<LifeRecord['status'], string> = {
  DRAFT: '작성 중',
  APPROVED: '원장 승인',
  ISSUED: '발급 완료',
};

function invalidate(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['life-records'] });
}

/**
 * 생활기록부 — 명세서 §5.13
 * 학부모: 발급본 열람 + PDF 저장.
 * 교직원: 원아별 초안 생성 → 종합의견·특이사항 작성 → 원장 승인 → 발급(표준 양식 PDF).
 */
export function LifeRecordsPage() {
  const role = useRole();
  const isParent = role === ROLES.PARENT;
  const isDirector = role === ROLES.DIRECTOR;
  const canWrite = useCanWrite();

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['life-records'],
    queryFn: () => apiData<LifeRecord[]>('/life-records'),
  });

  return (
    <Screen withNav={false}>
      <AppBar title="생활기록부" back />

      {!isParent && canWrite && <CreateSection />}

      {isPending && <Spinner />}
      {isError && (
        <ErrorNote message="생활기록부를 불러오지 못했어요." onRetry={() => void refetch()} />
      )}
      {!isPending && (data?.length ?? 0) === 0 && (
        <EmptyState title={EMPTY_STATES.lifeRecord.title} body={EMPTY_STATES.lifeRecord.body} />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {data?.map((record) => (
          <Card key={record.id} as="li">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold">
                  {record.child?.name ? `${record.child.name} · ` : ''}
                  {record.year}학년도 {record.term}회차
                </p>
                {record.issuedAt && (
                  <p className="text-sm text-ink-sub">{formatFullDate(record.issuedAt)} 발급</p>
                )}
              </div>
              <Chip tone={record.status === 'ISSUED' ? 'primary' : 'neutral'}>
                {STATUS_LABEL[record.status]}
              </Chip>
            </div>

            {/* 발급 전: 교직원은 여기서 작성·승인·발급까지 진행한다 */}
            {!isParent && record.status !== 'ISSUED' && canWrite ? (
              <RecordEditor record={record} isDirector={isDirector} />
            ) : (
              record.teacherComment && (
                <p className="mt-3 line-clamp-3 text-base text-ink">{record.teacherComment}</p>
              )
            )}

            {record.status === 'ISSUED' && (
              <>
                <PdfButton id={record.id} />
                {isDirector && canWrite && <ReissueButton id={record.id} />}
              </>
            )}

            {isParent && record.status !== 'ISSUED' && (
              <p className="mt-3 text-sm text-ink-sub">
                원에서 발급하면 여기에서 확인하고 저장하실 수 있어요.
              </p>
            )}
          </Card>
        ))}
      </ul>
    </Screen>
  );
}

/** 원아·회차를 골라 초안을 만든다 — 이미 있으면 그 기록을 그대로 보여 준다 */
function CreateSection() {
  const queryClient = useQueryClient();
  const { data: children } = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });

  const [childId, setChildId] = useState('');
  const [term, setTerm] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const activeChildId = childId || children?.[0]?.id || '';

  const create = useMutation({
    mutationFn: () =>
      api('/life-records', { method: 'POST', body: { childId: activeChildId, term } }),
    onSuccess: () => {
      setError(null);
      invalidate(queryClient);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '초안을 만들지 못했어요.'),
  });

  return (
    <div className="p-4 pb-0">
      <Card>
        <p className="mb-3 font-bold">생활기록부 만들기</p>

        <FieldGroup label="원아">
          <div className="flex flex-wrap gap-2">
            {children?.map((child) => (
              <Chip
                key={child.id}
                tone={child.id === activeChildId ? 'primary' : 'neutral'}
                selected={child.id === activeChildId}
                onClick={() => setChildId(child.id)}
              >
                {child.name}
              </Chip>
            ))}
          </div>
        </FieldGroup>

        <FieldGroup label="회차">
          <div className="flex gap-2">
            {[1, 2].map((t) => (
              <Chip
                key={t}
                tone={term === t ? 'primary' : 'neutral'}
                selected={term === t}
                onClick={() => setTerm(t)}
              >
                {t}회차
              </Chip>
            ))}
          </div>
        </FieldGroup>

        {error && <p className="mb-2 text-sm text-notice">{error}</p>}

        <Button
          block
          icon={<FilePlus2 size={16} />}
          disabled={!activeChildId}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          초안 만들기
        </Button>
        <p className="mt-2 text-xs text-ink-sub">
          출결·발달·신체계측은 자동으로 채워져요. 종합의견을 적고 원장님이 승인하면 표준 양식
          PDF로 발급됩니다.
        </p>
      </Card>
    </div>
  );
}

/** 발급 전 편집 — 종합의견·특이사항 저장, 원장 승인·발급 */
function RecordEditor({ record, isDirector }: { record: LifeRecord; isDirector: boolean }) {
  const queryClient = useQueryClient();
  const [teacherComment, setTeacherComment] = useState(record.teacherComment ?? '');
  const [remarks, setRemarks] = useState(record.remarks ?? '');
  const [error, setError] = useState<string | null>(null);

  const onError = (fallback: string) => (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : fallback);
  const onSuccess = () => {
    setError(null);
    invalidate(queryClient);
  };

  const save = useMutation({
    mutationFn: () =>
      api(`/life-records/${record.id}`, {
        method: 'PATCH',
        body: { teacherComment: teacherComment.trim(), remarks: remarks.trim() },
      }),
    onSuccess,
    onError: onError('저장하지 못했어요.'),
  });

  const approve = useMutation({
    mutationFn: () => api(`/life-records/${record.id}/approve`, { method: 'POST' }),
    onSuccess,
    onError: onError('승인하지 못했어요.'),
  });

  const issue = useMutation({
    mutationFn: () => api(`/life-records/${record.id}/issue`, { method: 'POST' }),
    onSuccess,
    onError: onError('발급하지 못했어요.'),
  });

  return (
    <div className="mt-3 flex flex-col gap-3">
      <Field label="담임 종합의견" hint="발급 전에 꼭 적어 주세요. 표준 양식의 ⑦종합의견에 실려요.">
        <Textarea
          rows={3}
          value={teacherComment}
          onChange={(e) => setTeacherComment(e.target.value)}
          placeholder="한 해 동안의 성장 모습을 강점 중심으로 적어 주세요."
        />
      </Field>

      <Field label="특이사항">
        <Textarea
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="알레르기, 건강상 유의점 등"
        />
      </Field>

      {error && <p className="text-sm text-notice">{error}</p>}

      <div className="flex gap-2">
        <Button variant="outline" block loading={save.isPending} onClick={() => save.mutate()}>
          저장
        </Button>

        {isDirector && record.status === 'DRAFT' && (
          <Button
            block
            icon={<CheckCircle2 size={16} />}
            loading={approve.isPending}
            onClick={() => approve.mutate()}
          >
            승인
          </Button>
        )}

        {isDirector && record.status === 'APPROVED' && (
          <Button
            block
            icon={<Stamp size={16} />}
            loading={issue.isPending}
            onClick={() => issue.mutate()}
          >
            발급하기
          </Button>
        )}
      </div>

      {!isDirector && (
        <p className="text-xs text-ink-sub">
          {record.status === 'DRAFT'
            ? '저장해 두면 원장님이 확인 후 승인·발급해요.'
            : '원장님 승인이 끝났어요. 발급을 기다리고 있어요.'}
        </p>
      )}
    </div>
  );
}

/**
 * 재발급 — 발급 후에 입력한 접종·병력·사진·계측을 스냅샷과 PDF에 다시 담는다.
 * 문서가 새로 생기는 건 아니라서 학부모에게 알림이 다시 가지는 않는다.
 */
function ReissueButton({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const reissue = useMutation({
    mutationFn: () => api(`/life-records/${id}/issue`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      invalidate(queryClient);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '재발급하지 못했어요.'),
  });

  return (
    <>
      <Button
        variant="outline"
        block
        className="mt-2"
        icon={<RefreshCw size={16} />}
        loading={reissue.isPending}
        onClick={() => reissue.mutate()}
      >
        최신 정보로 재발급
      </Button>
      <p className="mt-1.5 text-xs text-ink-sub">
        발급 후에 입력한 접종·병력·사진·계측을 다시 담아 PDF를 새로 만들어요.
      </p>
      {error && <p className="mt-1 text-sm text-notice">{error}</p>}
    </>
  );
}

function PdfButton({ id }: { id: string }) {
  async function open() {
    // 서명 URL은 만료가 짧으므로(§9.1) 누를 때마다 새로 받는다.
    const { url } = await apiData<{ url: string }>(`/life-records/${id}/pdf`);
    window.open(url, '_blank', 'noopener');
  }

  return (
    <Button variant="outline" block className="mt-3" icon={<Download size={16} />} onClick={() => void open()}>
      PDF로 저장하기
    </Button>
  );
}
